import { useMemo } from 'react';
import { useT } from '@/shared/lib/i18n';
import type { DomainEvent } from './types';

function eventTone(eventType: string) {
  if (eventType.includes('rejected') || eventType.includes('failed') || eventType.includes('removed')) {
    return 'bg-status-debt';
  }
  if (eventType.includes('payment') || eventType.includes('confirmed') || eventType.includes('joined')) {
    return 'bg-status-paid';
  }
  if (eventType.includes('requested') || eventType.includes('marked')) {
    return 'bg-status-warn';
  }
  return 'bg-brand-green';
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function payloadSummary(payload: Record<string, unknown> | null) {
  if (!payload) return '';
  const entries = Object.entries(payload)
    .filter(([, value]) => typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    .slice(0, 2);
  return entries.map(([key, value]) => `${key}: ${String(value)}`).join(' · ');
}

export function EventRow({ event }: { event: DomainEvent }) {
  const t = useT();
  const summary = useMemo(() => payloadSummary(event.payload), [event.payload]);

  return (
    <div className="flex gap-3 py-3 border-b border-[rgba(18,52,36,0.07)] last:border-b-0">
      <span className={`w-[9px] h-[9px] rounded-full mt-[5px] flex-shrink-0 ${eventTone(event.event_type)}`} />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium">{t(`event.types.${event.event_type}`)}</p>
        <p className="text-[11px] text-text-muted font-mono mt-0.5 truncate">
          {formatDate(event.created_at)}
          {summary ? ` · ${summary}` : ''}
        </p>
      </div>
    </div>
  );
}
